/**
 * me.ts — kid self/profile endpoints + mailbox + parent-vault + birthday
 * + parental controls (PR-15 split of api/client.ts).
 *
 * Anything keyed off "the logged-in child" that isn't pet/shop/economy
 * specific lives here: identity (getMe), device telemetry, scene
 * selection, mailbox CRUD, the parent-vault display the kid sees, and
 * the parental-controls read.
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts; consumers
 *     keep writing `api.getMe()`, `api.listMailbox()`, etc.
 *   - Used by AppShell / TopBar (getMe), MailboxPage, ChatPage (mail
 *     thread to parents), WalletPage (parent vault), and most pages
 *     that need balance / mail unread.
 *   - Backend: backend/src/routes/me.js, mailbox.js, parental_controls.js,
 *     parent_vault_child.js.
 *
 * 关键约束：
 *   - parental-controls + parent-vault + birthday endpoints all forward
 *     deviceTz() so daily caps and birthday windows match the kid's
 *     local clock, not the backend's KL default.
 */
import type {
  BirthdaySurprisePreview,
  CareerStats,
  Child,
  ParentalControls,
  Pet,
  PlayGate,
} from '@xiaopeiban/shared';
import { call, deviceTz } from './_core';

export interface ParentVaultForChild {
  total_balance: number;
  reminder_available: boolean;
  reminded_today: boolean;
  parents: Array<{
    id: number;
    email: string;
    balance: number;
    reminded_today: boolean;
  }>;
}

export interface MailMessage {
  id: number;
  family_id: number | null;
  parent_id: number | null;
  child_id: number | null;
  sender_type: 'system' | 'sysadmin' | 'parent' | 'child';
  sender_id: number | null;
  recipient_type: 'parent' | 'child';
  recipient_id: number;
  title: string;
  body: string;
  category: string;
  related_type: string | null;
  related_id: number | null;
  read_at: number | null;
  created_at: number;
}

export const meApi = {
  getMe() {
    return call<{
      child: Child;
      balance: number;
      active_pet: Pet | null;
      active_scene?: string;
      play_gate?: PlayGate;
      career_play_days?: number;
      career_stats?: CareerStats;
      mail_unread?: number;
    }>('/api/v1/child/me');
  },
  getParentVault() {
    return call<ParentVaultForChild>(
      `/api/v1/child/parent-vault?tz=${encodeURIComponent(deviceTz())}`,
    );
  },
  remindParentVault(reason_type: 'daily_vault_claim' | 'vault_insufficient' = 'daily_vault_claim') {
    return call<{
      ok: true;
      sent_count: number;
      skipped_count: number;
      message: string;
    }>('/api/v1/child/parent-vault/remind', {
      method: 'POST',
      body: JSON.stringify({ tz: deviceTz(), reason_type }),
    });
  },
  listMailbox() {
    return call<{ messages: MailMessage[] }>('/api/v1/child/mailbox');
  },
  markMailboxRead(id: number) {
    return call<{ ok: true; read_at: number }>(`/api/v1/child/mailbox/${id}/read`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  listParentMailThread(limit = 120) {
    return call<{ messages: MailMessage[] }>(
      `/api/v1/child/mailbox/parent-thread?limit=${encodeURIComponent(String(limit))}`,
    );
  },
  sendParentMail(body: string, title = '写给爸爸妈妈的信') {
    return call<{ ok: true; sent_count: number; messages: MailMessage[] }>(
      '/api/v1/child/mailbox/send-parent',
      { method: 'POST', body: JSON.stringify({ title, body }) },
    );
  },
  getInviteInfo() {
    return call<{
      invite_code: string;
      invited_count: number;
      reward_points_earned: number;
      child_reward_points: number;
      parent_welcome_credits: number;
    }>('/api/v1/child/invite');
  },
  getMyParentalControls() {
    return call<{
      controls: ParentalControls;
      base_controls?: ParentalControls;
      birthday_bonus?: {
        extra_play_minutes: number;
        birthday_surprise: BirthdaySurprisePreview;
      } | null;
    }>(`/api/v1/child/parental-controls?tz=${encodeURIComponent(deviceTz())}`);
  },
  birthdaySurprise() {
    return call<{ birthday_surprise: BirthdaySurprisePreview }>(
      `/api/v1/child/birthday-surprise?tz=${encodeURIComponent(deviceTz())}`,
    );
  },
  claimBirthdaySurprise() {
    return call<{
      ok: true;
      already_claimed?: boolean;
      birthday_surprise: BirthdaySurprisePreview;
    }>('/api/v1/child/birthday-surprise/claim', {
      method: 'POST',
      body: JSON.stringify({ tz: deviceTz() }),
    });
  },
  updateMe(body: { display_name?: string | null; avatar?: string | null }) {
    return call<{ ok: true }>('/api/v1/child/me', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  reportDevice(body: Record<string, unknown>) {
    return call<{ ok: true }>('/api/v1/child/device', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  setActiveScene(scene_key: string) {
    return call<{ ok: true; active_scene: string }>('/api/v1/child/scene/active', {
      method: 'POST', body: JSON.stringify({ scene_key }),
    });
  },
};
